import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { AppButton, Box, Checkbox, costFormater, Responsive, SimpleHint, Text, useResponsive, WarningIcon } from '@common-fe/common-fe';
import _ from 'lodash';

import ROUTES from '@/common/routes';
import { ValuesMap } from '@/common/types';
import { SuccessModal } from '@/components/elements';
import ServerErrorModal from '@/modules/investments/modal/ServerErrorModal';

import { SelfDirectedFooter } from '../../SelfDirectedAllocation';

import useSellInvestments from './hooks/useSellInvestments';
import ConfirmModal from './ConfirmModal';
import { PERCENTS_FULL_LIQUIDATION, PERCENTS_LIQUIDATION_LIMIT } from './Funds.constants';
import FundsHeader from './FundsHeader';
import FundsItem from './FundsItem';
import { useSellFunds } from './hooks';

interface Props {
  accountId: string;
}

const Funds: React.FC<Props> = ({ accountId }) => {
  const { isMobile } = useResponsive();
  const history = useHistory();
  const [isConfirmVisible, setConfirmVisible] = useState(false);
  const [isSuccessModal, setSuccessModal] = useState(false);
  const [isServerErrorModal, setServerErrorModal] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const { data } = useSellFunds({
    accountId,
  });

  const totalAmount = useMemo(() => {
    return data.reduce((result, item) => {
      if (item.isFullLiquidation) {
        return result;
      }
      return result + (item.amount || 0);
    }, 0);
  }, [data]);

  const [isLiquidAll, setLiquidAll] = useState(false);
  const [values, setValues] = useState<ValuesMap<number>>({});
  const [liquidationValues, setLiquidationValues] = useState<ValuesMap<boolean>>({});
  const [fullLiquidationValues, setFullLiquidationValuesValues] = useState<ValuesMap<boolean>>({});
  const { t } = useTranslation();

  const isFundsError = useMemo(() => {
    if (Object.values(values).length === 0) return true;
    if (Object.values(values).some((item) => item !== 0)) return false;
    return true;
  }, [values]);

  const selectedSum = useMemo(() => {
    return _.sum(Object.values(values));
  }, [values]);
  const isForceLiquidation = useMemo(() => {
    const totalCurrentValues = selectedSum;
    const preparedTotalAmount = parseFloat(totalAmount.toFixed(2));
    const preparedCurrentValues = parseFloat(totalCurrentValues.toFixed(2));
    const percents = preparedCurrentValues / preparedTotalAmount;
    const isAllFundsLiquidated = Object.values(fullLiquidationValues).every((item) => item);
    if (_.isNaN(percents)) {
      return false;
    }
    return (
      percents >= PERCENTS_LIQUIDATION_LIMIT && percents !== PERCENTS_FULL_LIQUIDATION && isAllFundsLiquidated
    );
  }, [selectedSum, totalAmount, fullLiquidationValues]);
  const handleChangeValue = useCallback(
    (id: string) => (value: number) => {
      setValues({
        ...values,
        [id]: value,
      });
    },
    [values]
  );

  const handleChangeLiquidationValue = useCallback(
    (id: string) => (value: boolean) => {
      setLiquidationValues({
        ...liquidationValues,
        [id]: value,
      });
    },
    [liquidationValues]
  );

  const handleCancel = useCallback(() => {
    history.push(`${ROUTES.INVESTMENTS}?id=${accountId}`);
  }, [history, accountId]);

  const handleSubmit = useCallback(() => {
    setIsSubmitted(true);
    if (!isFundsError) {
      setConfirmVisible(true);
    }
  }, [isFundsError]);

  const { handleSellInvestments, isLoading } = useSellInvestments();

  const handleSave = useCallback(async () => {
    try {
      const formattedList = Object.keys(values).map((key) => {
        const itemValue = values[key];
        const foundItem = data.find((item) => item.id === key);
        if (foundItem) {
          const currentAmountCash = itemValue;

          if (fullLiquidationValues[key]) {
            return {
              instrumentId: key,
              isFullLiquidation: true,
            };
          }
          return {
            instrumentId: key,
            amountCash: _.round(currentAmountCash, 2),
          };
        }
        return {
          instrumentId: key,
          quantity: 0,
        };
      });
      await handleSellInvestments({
        isFullLiquidation: isLiquidAll,
        preOrders: formattedList,
      });
      setConfirmVisible(false);
      setSuccessModal(true);
    } catch {
      setConfirmVisible(false);
      setServerErrorModal(true);
    }
  }, [values, handleSellInvestments, isLiquidAll, data, fullLiquidationValues]);

  const handleCloseSuccess = useCallback(() => {
    setSuccessModal(false);
    history.push(`${ROUTES.INVESTMENTS}?id=${accountId}`);
  }, [accountId, history]);

  useEffect(() => {
    const ids = data.map(({ id }) => id);
    if (isLiquidAll) {
      setLiquidationValues(
        ids.reduce(
          (result, id) => ({
            ...result,
            [id]: true,
          }),
          {}
        )
      );
    } else {
      setLiquidationValues(
        ids.reduce(
          (result, id) => ({
            ...result,
            [id]: false,
          }),
          {}
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLiquidAll]);

  useEffect(() => {
    if (isForceLiquidation) {
      setLiquidAll(false);
    }

    if (Object.values(liquidationValues).some((item) => !item)) {
      setLiquidAll(false);
    }
  }, [isForceLiquidation, liquidationValues]);

  useEffect(() => {
    const values = Object.values(liquidationValues);
    const allValuesExist = values.length > 0 && values.length === data.length;
    const isAllTrue = values.every((item) => item);
    if (allValuesExist && isAllTrue) {
      setLiquidAll(true);
    }
  }, [liquidationValues, data]);

  useEffect(() => {
    const liquidate = Object.keys(values).reduce((result, item) => {
      const currentValue = values[item];
      const foundItem = data.find((dataItem) => dataItem.id === item);

      if (foundItem && foundItem.amount) {
        const percents = currentValue / foundItem.amount;

        if (Number.isNaN(percents)) {
          return {
            ...result,
            [item]: false,
          };
        }

        return {
          ...result,
          [item]: percents >= PERCENTS_LIQUIDATION_LIMIT,
        };
      }

      return result;
    }, {});

    setFullLiquidationValuesValues(liquidate);
  }, [data, values]);

  const handleCheckAll = useCallback(
    (value: boolean) => {
      setLiquidAll(value);
      if (value) {
        setValues(
          data.reduce((result, item) => {
            if (item.isFullLiquidation) {
              return result;
            }
            return {
              ...result,
              [item.id]: item.amount,
            };
          }, {})
        );
        return;
      }
      setValues(
        data.reduce((result, item) => {
          if (item.isFullLiquidation) {
            return result;
          }
          return {
            ...result,
            [item.id]: 0,
          };
        }, {})
      );
    },
    [data]
  );
  return (
    <Box
      background={isMobile ? 'canvas' : 'module'}
      margin={{ vertical: 'spacing24' }}
      round="moduleRound"
      direction="column"
    >
      <ConfirmModal
        liquidationValues={liquidationValues}
        visible={isConfirmVisible}
        isLoading={isLoading}
        onSetVisible={setConfirmVisible}
        onSubmit={handleSave}
        values={values}
        list={data}
      />

      <ServerErrorModal
        visible={isServerErrorModal}
        onSetVisible={setServerErrorModal}
        onSendRequest={handleSave}
      />
      <SuccessModal
        testId="success"
        onSetVisible={handleCloseSuccess}
        buttonText={t('Go to Investment Dashboard')}
        visible={isSuccessModal}
        onSubmit={handleCloseSuccess}
        header={t(['Submitted!'])}
      />
      <Box
        background={isMobile ? 'module' : undefined}
        round="moduleRound"
        pad={isMobile ? 'spacing16' : 'spacing24'}
      >
        <Box direction={isMobile ? 'column' : 'row'} justify={isMobile ? 'start' : 'between'}>
          <Text size="large" weight={700} data-testid="sell-investments_total-title">
            {t('Holdings: ')} {data.length}
          </Text>
          <Box
            direction={isMobile ? 'row-reverse' : 'row'}
            justify={isMobile ? 'end' : undefined}
            align="center"
            margin={isMobile ? { top: 'spacing8' } : {}}
          >
            {isForceLiquidation && (
              <SimpleHint
                testId="sell-investments_liquidation-hint"
                content={
                  <Text data-testId="sell-investments_liquidation-hint-text">
                    {t('Your request exceeds 95% of the portfolio. All holdings will be sold.')}
                  </Text>
                }
              >
                <Box margin={{ left: 'xs' }}>
                  <WarningIcon color="warning" />
                </Box>
              </SimpleHint>
            )}

            <Text>{t('Full portfolio liquidation')}</Text>

            <Box margin={isMobile ? {} : { left: 'xs' }}>
              <Checkbox
                testId="sell-investments"
                name="all"
                checked={isLiquidAll || isForceLiquidation}
                disabled={isForceLiquidation}
                onChange={handleCheckAll}
              />
            </Box>
          </Box>
        </Box>

        <Box direction="column">
          <Responsive sizes={[Responsive.Tablet, Responsive.Desktop]}>
            <FundsHeader />
          </Responsive>

          {data.map((item) => (
            <FundsItem
              testId={`sell-investments_item_${item.id}`}
              key={item.id}
              {...item}
              isLiquidationAllMode={isLiquidAll}
              value={values[item.id]}
              isPendingLiquidation={item.isFullLiquidation}
              onChangeValue={item.isFullLiquidation ? () => {} : handleChangeValue(item.id)}
              isLiquidation={liquidationValues[item.id] || isLiquidAll}
              onSetLiquidation={handleChangeLiquidationValue(item.id)}
            />
          ))}

          <Responsive size={Responsive.Mobile}>
            <Box
              margin={{ top: 'spacing12' }}
              direction="row"
              align="center"
              justify="between"
              pad="spacing12"
              round="container2Round"
              background="border1"
            >
              <Text size="large" weight={700}>
                {t('Total')}
              </Text>
              <Text size="large" weight={700}>
                {costFormater(selectedSum, {
                  isPrecision: true,
                })}
              </Text>
            </Box>
          </Responsive>
        </Box>
      </Box>
      <Box
        direction="column"
        pad={isMobile ? { top: 'spacing4', left: 'spacing16', right: 'spacing16' } : 'spacing24'}
      >
        {isFundsError && isSubmitted && (
          <Box alignSelf="end" margin={isMobile ? {} : { top: 'spacing24' }}>
            <Text size="medium" color="danger">
              {t('Please enter the amount to sell')}
            </Text>
          </Box>
        )}
        <Box
          direction="row"
          width="100%"
          justify="end"
          pad={isMobile ? { top: 'spacing12' } : {}}
          gap="spacing12"
        >
          <Box fill={isMobile}>
            <AppButton
              fill={isMobile}
              onClick={handleCancel}
              testId="sell-investments_cancel"
              width="140px"
              buttonType="secondary"
            >
              {t('Cancel')}
            </AppButton>
          </Box>

          <Box fill={isMobile}>
            <AppButton fill={isMobile} onClick={handleSubmit} testId="sell-investments_submit" width="140px">
              {t('Submit')}
            </AppButton>
          </Box>
        </Box>

        <SelfDirectedFooter />
      </Box>
    </Box>
  );
};

export default Funds;
